import React from 'react';
import { useLoadScript, Autocomplete } from '@react-google-maps/api';
import PropTypes from 'prop-types';

const libraries = ['places'];

const LocationStep = ({ formData, handleInputChange, errors, updateFormData }) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const [autocomplete, setAutocomplete] = React.useState(null);

  const onLoad = (autoC) => setAutocomplete(autoC);

  const onPlaceChanged = () => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();
      let newFormData = {
        ...formData,
        deliveryAddress: place.formatted_address,
        addressName: place.name,
        streetAddress: '',
        city: '',
        postalCode: '',
        latitude: place.geometry?.location.lat() || '',
        longitude: place.geometry?.location.lng() || ''
      };
      
      for (const component of place.address_components) {
        const componentType = component.types[0];
        switch (componentType) {
          case 'street_number':
            newFormData.streetAddress = component.long_name + ' ';
            break;
          case 'route':
            newFormData.streetAddress += component.long_name;
            break;
          case 'locality':
            newFormData.city = component.long_name;
            break;
          case 'postal_code':
            newFormData.postalCode = component.long_name;
            break;
          default:
            break;
        }
      }
      updateFormData(newFormData);
    }
  };

  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) return <div>Loading maps</div>;

  return (
    <div>
      <div className="mb-4">
        <label htmlFor="deliveryAddress" className="block text-gray-700 text-sm font-bold mb-2">
          Delivery Address (Australia only)
        </label>
        <Autocomplete
          onLoad={onLoad}
          onPlaceChanged={onPlaceChanged}
          restrictions={{ country: "au" }}
          fields={["address_components", "geometry", "name", "formatted_address"]}
        >
          <input
            type="text"
            id="deliveryAddress"
            name="deliveryAddress"
            value={formData.deliveryAddress || ''}
            onChange={handleInputChange}
            placeholder="Enter the delivery address in Australia"
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            required
          />
        </Autocomplete>
        {errors.deliveryAddress && <p className="mt-1 text-sm text-red-500">{errors.deliveryAddress}</p>}
      </div>
      <div className="mb-4">
        <label htmlFor="specialConditions" className="block text-gray-700 text-sm font-bold mb-2">
          Special Delivery Conditions
        </label>
        <textarea
          id="specialConditions"
          name="specialConditions"
          value={formData.specialConditions || ''}
          onChange={handleInputChange}
          placeholder="Enter any special delivery conditions (e.g., remote areas, difficult access)"
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          rows="3"
        />
      </div>
      <div className="mb-4">
        <label htmlFor="siteType" className="block text-gray-700 text-sm font-bold mb-2">
          Site Type
        </label>
        <select
          id="siteType"
          name="siteType"
          value={formData.siteType || ''}
          onChange={handleInputChange}
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          required
        >
          <option value="">Select site type</option>
          <option value="indoor">Indoor</option>
          <option value="outdoor">Outdoor</option>
        </select>
        {errors.siteType && <p className="mt-1 text-sm text-red-500">{errors.siteType}</p>}
      </div>
      <div className="mb-4">
        <label htmlFor="powerSupply" className="block text-gray-700 text-sm font-bold mb-2">
          Power Supply Available
        </label>
        <select
          id="powerSupply"
          name="powerSupply"
          value={formData.powerSupply || ''}
          onChange={handleInputChange}
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          required
        >
          <option value="">Select power supply option</option>
          <option value="available">Available</option>
          <option value="not_available">Not Available</option>
          <option value="unsure">Unsure</option>
        </select>
        {errors.powerSupply && <p className="mt-1 text-sm text-red-500">{errors.powerSupply}</p>}
      </div>
    </div>
  );
};

LocationStep.propTypes = {
  formData: PropTypes.object.isRequired,
  handleInputChange: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  updateFormData: PropTypes.func.isRequired,
};

export default LocationStep;